import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { API_BASE } from '../config';
import { identifyUser, track } from '../utils/analytics';
import { ROLE_OPTIONS } from '../utils/roleLabel';
const API = API_BASE;

function EyeIcon({ open }) {
  return open ? (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
      <circle cx="12" cy="12" r="3"/>
    </svg>
  ) : (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
      <line x1="1" y1="1" x2="23" y2="23"/>
    </svg>
  );
}

function PasswordInput({ value, onChange, placeholder, autoComplete, style }) {
  const [visible, setVisible] = useState(false);
  return (
    <div style={{ position: 'relative' }}>
      <input
        type={visible ? 'text' : 'password'}
        value={value}
        onChange={onChange}
        required
        placeholder={placeholder}
        autoComplete={autoComplete}
        style={{ ...style, paddingRight: '2.5rem' }}
      />
      <button
        type="button"
        onClick={() => setVisible(v => !v)}
        style={{
          position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)',
          background: 'none', border: 'none', cursor: 'pointer', color: '#aaa',
          display: 'flex', alignItems: 'center', padding: 0, width: 'auto',
        }}
        tabIndex={-1}
      >
        <EyeIcon open={visible} />
      </button>
    </div>
  );
}

export default function Signup() {
  const navigate = useNavigate();
  // Prefill email when the user lands here from the beta-welcome page,
  // which appends ?email=... after they accept the beta terms. Read once
  // at mount via the lazy-init form so we don't re-parse on every render.
  const [email, setEmail] = useState(() => {
    if (typeof window === 'undefined') return '';
    const params = new URLSearchParams(window.location.search);
    return (params.get('email') || '').trim().toLowerCase();
  });
  const [password, setPassword] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [roleSelection, setRoleSelection] = useState('');
  const [customRole, setCustomRole] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  // After successful register, switch the page to a 2FA-offer step
  // rather than navigating straight to the dashboard.
  const [registered, setRegistered] = useState(false);

  // Business-profile step (between register and 2FA offer). If the
  // register response shows all three demographic fields already filled
  // (i.e. they came from a beta_signups copy at registration time), we
  // skip this step entirely. Otherwise the user gets a one-screen ask.
  const [profileDone, setProfileDone] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [teamSizeBand, setTeamSizeBand] = useState('');
  const [annualRevenueBand, setAnnualRevenueBand] = useState('');
  const [topChallenge, setTopChallenge] = useState('');

  const TEAM_SIZE_BANDS = ['1-10', '11-50', '51-200', '200+'];
  const REVENUE_BANDS = ['Under £1M', '£1-5M', '£5-20M', '£20M+'];
  const CHALLENGE_OPTIONS = [
    "We've hit a growth plateau",
    "We're scaling and need clearer alignment",
    "We need to make a big strategic call soon",
    "We're rebuilding direction after a pivot",
  ];

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    setLoading(true);
    try {
      const roleLabel = roleSelection === 'Other'
        ? (customRole.trim() || null)
        : (roleSelection || null);
      const cleanWebsiteUrl = (() => {
        const v = (websiteUrl || '').trim();
        if (!v) return '';
        return /^https?:\/\//i.test(v) ? v : `https://${v}`;
      })();
      const res = await axios.post(`${API}/auth/register`, { email, password, businessName, websiteUrl: cleanWebsiteUrl, roleLabel });
      localStorage.setItem('guruone_token', res.data.token);
      localStorage.setItem('guruone_user', JSON.stringify(res.data.user));
      track('user_registered', { hasWebsite: !!websiteUrl, hasBusinessName: !!businessName, hasRoleLabel: !!roleLabel });
      identifyUser(res.data.user.id, res.data.user.email, res.data.user.businessName);
      // If the register response already has all three demographic
      // fields populated (copied from a matching beta_signups row),
      // skip the business-profile step. Otherwise pre-fill whatever
      // IS present so the user only has to answer what's missing.
      const u = res.data.user || {};
      if (u.teamSizeBand && u.annualRevenueBand && u.currentTopChallenge) {
        setProfileDone(true);
      } else {
        if (u.teamSizeBand) setTeamSizeBand(u.teamSizeBand);
        if (u.annualRevenueBand) setAnnualRevenueBand(u.annualRevenueBand);
        if (u.currentTopChallenge) setTopChallenge(u.currentTopChallenge);
      }
      setRegistered(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Sign up failed. Please try again.');
    }
    setLoading(false);
  }

  function handleSetUp2FA() {
    track('signup_2fa_set_up_now');
    navigate('/two-factor-setup');
  }
  function handleSkip2FA() {
    track('signup_2fa_skipped');
    navigate('/dashboard');
  }

  async function handleSaveProfile() {
    setProfileError('');
    setProfileSaving(true);
    try {
      const body = {};
      if (teamSizeBand) body.teamSizeBand = teamSizeBand;
      if (annualRevenueBand) body.annualRevenueBand = annualRevenueBand;
      if (topChallenge) body.currentTopChallenge = topChallenge;
      if (Object.keys(body).length === 0) {
        // Nothing to save — treat as a skip.
        track('signup_profile_skipped', { reason: 'no_fields' });
        setProfileDone(true);
        return;
      }
      const res = await axios.patch(`${API}/auth/me`, body);
      localStorage.setItem('guruone_user', JSON.stringify(res.data.user));
      track('signup_profile_saved', {
        hasTeamSize: !!teamSizeBand,
        hasRevenue: !!annualRevenueBand,
        hasChallenge: !!topChallenge,
      });
      setProfileDone(true);
    } catch (err) {
      setProfileError(err.response?.data?.error || 'Could not save. Try again or skip for now.');
    } finally {
      setProfileSaving(false);
    }
  }

  function handleSkipProfile() {
    track('signup_profile_skipped', { reason: 'user_skipped' });
    setProfileDone(true);
  }

  const inputStyle = {
    width: '100%', padding: '0.75rem 0.9rem', borderRadius: 8,
    border: '1.5px solid #e5e7eb', fontSize: '0.9rem', fontFamily: 'inherit',
    boxSizing: 'border-box', outline: 'none', transition: 'border-color 0.15s',
  };

  return (
    <div style={{
      minHeight: '100vh', background: '#f7f7f9',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '2rem',
    }}>
      {/* Brand */}
      <div style={{ marginBottom: '2rem', textAlign: 'center' }}>
        <img src="/Logo.png" alt="GuruOne" style={{ height: 88, display: 'block', margin: '0 auto 0.75rem' }} />
        <div style={{ fontSize: '0.82rem', color: '#aaa' }}>Your virtual C-suite</div>
      </div>

      {/* Card */}
      <div style={{ background: 'white', borderRadius: 16, padding: '2.5rem', width: '100%', maxWidth: 420, boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
        {registered && !profileDone ? (
          <>
            <h2 style={{ margin: '0 0 0.5rem', fontSize: '1.25rem', color: '#1a1a2e', fontWeight: 700 }}>
              A bit about your business
            </h2>
            <p style={{ margin: '0 0 1.25rem', fontSize: '0.85rem', color: '#555', lineHeight: 1.55 }}>
              Three quick questions so GuruOne can tailor what comes next.
              Your top challenge will be added as a starter item on your Direction canvas.
            </p>

            <div style={{ marginBottom: '1.1rem' }}>
              <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#1a1a2e', marginBottom: '0.4rem' }}>
                Team size
              </label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {TEAM_SIZE_BANDS.map(b => (
                  <button key={b} type="button" onClick={() => setTeamSizeBand(b)}
                    style={{
                      padding: '0.45rem 0.85rem', borderRadius: 8, cursor: 'pointer',
                      fontFamily: 'inherit', fontSize: '0.82rem', fontWeight: 600,
                      background: teamSizeBand === b ? '#1D9E75' : 'white',
                      color: teamSizeBand === b ? 'white' : '#333',
                      border: teamSizeBand === b ? '1.5px solid #1D9E75' : '1.5px solid #e5e7eb',
                    }}>
                    {b}
                  </button>
                ))}
              </div>
            </div>

            <div style={{ marginBottom: '1.1rem' }}>
              <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#1a1a2e', marginBottom: '0.4rem' }}>
                Annual revenue
              </label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {REVENUE_BANDS.map(b => (
                  <button key={b} type="button" onClick={() => setAnnualRevenueBand(b)}
                    style={{
                      padding: '0.45rem 0.85rem', borderRadius: 8, cursor: 'pointer',
                      fontFamily: 'inherit', fontSize: '0.82rem', fontWeight: 600,
                      background: annualRevenueBand === b ? '#1D9E75' : 'white',
                      color: annualRevenueBand === b ? 'white' : '#333',
                      border: annualRevenueBand === b ? '1.5px solid #1D9E75' : '1.5px solid #e5e7eb',
                    }}>
                    {b}
                  </button>
                ))}
              </div>
            </div>

            <div style={{ marginBottom: '1.25rem' }}>
              <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#1a1a2e', marginBottom: '0.4rem' }}>
                Top challenge right now
              </label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {CHALLENGE_OPTIONS.map(c => (
                  <button key={c} type="button" onClick={() => setTopChallenge(c)}
                    style={{
                      padding: '0.55rem 0.85rem', borderRadius: 8, cursor: 'pointer',
                      fontFamily: 'inherit', fontSize: '0.82rem', textAlign: 'left',
                      background: topChallenge === c ? '#f0faf6' : 'white',
                      color: topChallenge === c ? '#0F6E56' : '#333',
                      border: topChallenge === c ? '1.5px solid #1D9E75' : '1.5px solid #e5e7eb',
                      fontWeight: topChallenge === c ? 600 : 400,
                    }}>
                    {c}
                  </button>
                ))}
              </div>
            </div>

            {profileError && (
              <div style={{
                background: '#fef2f2', color: '#991b1b', padding: '0.6rem 0.85rem',
                borderRadius: 8, fontSize: '0.82rem', marginBottom: '0.85rem',
              }}>{profileError}</div>
            )}

            <button
              onClick={handleSaveProfile}
              disabled={profileSaving}
              style={{
                padding: '0.85rem', borderRadius: 8, background: '#1D9E75', color: 'white',
                border: 'none', fontSize: '0.95rem', fontWeight: 600, width: '100%',
                cursor: profileSaving ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
                marginBottom: '0.6rem', opacity: profileSaving ? 0.6 : 1,
              }}>
              {profileSaving ? 'Saving…' : 'Save and continue →'}
            </button>
            <button
              onClick={handleSkipProfile}
              disabled={profileSaving}
              style={{
                padding: '0.65rem', borderRadius: 8, background: 'white', color: '#666',
                border: '1px solid #e0e0e0', fontSize: '0.85rem', width: '100%',
                cursor: profileSaving ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
              }}>
              Skip for now
            </button>
          </>
        ) : registered ? (
          <>
            <h2 style={{ margin: '0 0 0.5rem', fontSize: '1.25rem', color: '#1a1a2e', fontWeight: 700 }}>
              Account created ✓
            </h2>
            <p style={{ margin: '0 0 1.4rem', fontSize: '0.88rem', color: '#444', lineHeight: 1.55 }}>
              Add an extra layer of security with two-factor authentication.
              We'd recommend it — it takes about a minute with an authenticator
              app like Google Authenticator or 1Password.
            </p>
            <button
              onClick={handleSetUp2FA}
              style={{
                padding: '0.85rem', borderRadius: 8, background: '#1D9E75', color: 'white',
                border: 'none', fontSize: '0.95rem', fontWeight: 600, width: '100%',
                cursor: 'pointer', fontFamily: 'inherit', marginBottom: '0.6rem',
              }}>
              Set up two-factor now →
            </button>
            <button
              onClick={handleSkip2FA}
              style={{
                padding: '0.65rem', borderRadius: 8, background: 'white', color: '#666',
                border: '1px solid #e0e0e0', fontSize: '0.85rem', width: '100%',
                cursor: 'pointer', fontFamily: 'inherit',
              }}>
              Skip — I'll do this later from Settings
            </button>
            <p style={{ margin: '1rem 0 0', fontSize: '0.72rem', color: '#bbb', textAlign: 'center', lineHeight: 1.5 }}>
              You can enable two-factor any time from <strong>Settings</strong> in the header.
            </p>
          </>
        ) : (
          <>
        <h2 style={{ margin: '0 0 0.5rem', fontSize: '1.25rem', color: '#1a1a2e', fontWeight: 700 }}>
          Create your account
        </h2>
        <p style={{ margin: '0 0 1.75rem', fontSize: '0.82rem', color: '#888' }}>
          Free to start. No credit card needed.
        </p>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div>
            <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#1a1a2e', marginBottom: '0.4rem' }}>
              Business name <span style={{ color: '#aaa', fontWeight: 400 }}>(optional)</span>
            </label>
            <input
              type="text" value={businessName} onChange={e => setBusinessName(e.target.value)}
              placeholder="e.g. Acme Ltd" style={inputStyle}
            />
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#1a1a2e', marginBottom: '0.4rem' }}>
              Business website <span style={{ color: '#aaa', fontWeight: 400 }}>(optional — we'll pre-fill your canvases)</span>
            </label>
            <input
              type="text" value={websiteUrl} onChange={e => setWebsiteUrl(e.target.value)}
              placeholder="yourbusiness.com" style={inputStyle}
            />
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#1a1a2e', marginBottom: '0.4rem' }}>
              How do you describe your role? <span style={{ color: '#aaa', fontWeight: 400 }}>(optional)</span>
            </label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: roleSelection === 'Other' ? 8 : 0 }}>
              {ROLE_OPTIONS.map((opt) => {
                const isSel = roleSelection === opt;
                return (
                  <button key={opt} type="button" onClick={() => setRoleSelection(opt)}
                    style={{
                      padding: '0.35rem 0.8rem', fontSize: '0.76rem', borderRadius: 18,
                      border: isSel ? '1.5px solid #1D9E75' : '1.5px solid #e5e7eb',
                      background: isSel ? '#e6f5ec' : 'white',
                      color: isSel ? '#0F6E56' : '#444',
                      fontWeight: isSel ? 600 : 500,
                      cursor: 'pointer', fontFamily: 'inherit', width: 'auto',
                    }}>
                    {opt}
                  </button>
                );
              })}
              <button type="button" onClick={() => setRoleSelection('Other')}
                style={{
                  padding: '0.35rem 0.8rem', fontSize: '0.76rem', borderRadius: 18,
                  border: roleSelection === 'Other' ? '1.5px solid #1D9E75' : '1.5px solid #e5e7eb',
                  background: roleSelection === 'Other' ? '#e6f5ec' : 'white',
                  color: roleSelection === 'Other' ? '#0F6E56' : '#444',
                  fontWeight: roleSelection === 'Other' ? 600 : 500,
                  cursor: 'pointer', fontFamily: 'inherit', width: 'auto',
                }}>
                Other
              </button>
            </div>
            {roleSelection === 'Other' && (
              <input
                type="text" value={customRole} onChange={e => setCustomRole(e.target.value)}
                placeholder="e.g. Chief of Staff, Partner, Co-Founder"
                maxLength={60} style={inputStyle}
              />
            )}
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#1a1a2e', marginBottom: '0.4rem' }}>
              Email
            </label>
            <input
              type="email" value={email} onChange={e => setEmail(e.target.value)}
              required placeholder="you@example.com" autoComplete="email" style={inputStyle}
            />
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#1a1a2e', marginBottom: '0.4rem' }}>
              Password
            </label>
            <PasswordInput
              value={password} onChange={e => setPassword(e.target.value)}
              placeholder="At least 8 characters" autoComplete="new-password" style={inputStyle}
            />
          </div>

          {error && (
            <div style={{ background: '#FEE2E2', border: '1px solid #FECACA', borderRadius: 8, padding: '0.65rem 0.9rem', fontSize: '0.82rem', color: '#991B1B' }}>
              {error}
            </div>
          )}

          <button
            type="submit" disabled={loading || !email || !password}
            style={{
              padding: '0.85rem', borderRadius: 8, background: '#1D9E75', color: 'white',
              border: 'none', fontSize: '0.95rem', fontWeight: 600, width: '100%',
              cursor: loading || !email || !password ? 'not-allowed' : 'pointer',
              opacity: loading || !email || !password ? 0.6 : 1,
              fontFamily: 'inherit', marginTop: '0.5rem', transition: 'opacity 0.15s',
            }}>
            {loading ? 'Creating account...' : 'Create account →'}
          </button>
        </form>

        <p style={{ fontSize: '0.72rem', color: '#bbb', textAlign: 'center', marginTop: '1rem', lineHeight: 1.5 }}>
          Your business data is saved securely to your account and never used to train AI models.
        </p>

        <div style={{ textAlign: 'center', marginTop: '1.25rem', fontSize: '0.82rem', color: '#888' }}>
          Already have an account?{' '}
          <Link to="/login" style={{ color: '#1D9E75', fontWeight: 600, textDecoration: 'none' }}>Log in</Link>
        </div>
          </>
        )}
      </div>
    </div>
  );
}
